(() => {
  if (customElements.get('caha-xmas-shelf')) return;
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  class Shelf extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController(); const signal = this.abort.signal;
      this.list = this.querySelector('.cx-list'); this.cards = [...this.querySelectorAll('.cx-card')];
      this.tabs = [...this.querySelectorAll('[data-filter]')]; this.more = this.querySelector('.cx-more');
      this.filter = 'all'; this.limit = Number(this.dataset.initial) || 12;
      this.tabs.forEach(tab => tab.addEventListener('click', () => {
        this.filter = tab.dataset.filter; this.limit = Number(this.dataset.initial) || 12;
        this.tabs.forEach(t => t.setAttribute('aria-pressed', String(t === tab)));
        this.paint(true); this.list.scrollTo({left:0,behavior:'instant'});
      }, {signal}));
      this.querySelectorAll('[data-step]').forEach(button => button.addEventListener('click', () => {
        const card = this.cards.find(c => !c.hidden);
        const step = card ? card.offsetWidth + parseFloat(getComputedStyle(this.list).gap) : this.list.clientWidth;
        this.list.scrollBy({left:Number(button.dataset.step) * step,behavior:reduced()?'instant':'smooth'});
      }, {signal}));
      this.list.addEventListener('scroll', () => this.arrows(), {signal,passive:true});
      this.resize = new ResizeObserver(() => this.arrows()); this.resize.observe(this.list);
      this.more?.addEventListener('click', () => {
        const previous = this.cards.filter(c => !c.hidden).length;
        this.limit += 12; this.paint(true);
        this.cards.filter(c => !c.hidden)[previous]?.querySelector('a')?.focus({preventScroll:true});
      }, {signal});
      this.paint(false);
    }
    paint(announce) {
      let count = 0;
      this.cards.forEach(card => {
        const matches = this.filter === 'all' || card.dataset.category === this.filter;
        if (matches) count++;
        card.hidden = !matches || (this.dataset.layout === 'grid' && count > this.limit);
      });
      this.querySelector('.cx-empty').hidden = count !== 0;
      if (this.more) this.more.hidden = count <= this.limit;
      if (announce) { const status = this.querySelector('.cx-status'); status.textContent = count + ' ' + status.dataset.label; }
      requestAnimationFrame(() => this.arrows());
    }
    arrows() {
      this.querySelector('[data-step="-1"]')?.toggleAttribute('disabled',this.list.scrollLeft <= 2);
      this.querySelector('[data-step="1"]')?.toggleAttribute('disabled',this.list.scrollLeft >= this.list.scrollWidth-this.list.clientWidth-2);
    }
    disconnectedCallback() { this.abort?.abort(); this.resize?.disconnect(); }
  }
  class Films extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController(); const signal = this.abort.signal;
      this.videos = [...this.querySelectorAll('video')];
      this.videos.forEach(video => video.addEventListener('play', () => this.videos.forEach(v => {if(v !== video)v.pause();}),{signal}));
      document.addEventListener('visibilitychange', () => { if(document.hidden)this.videos.forEach(v => v.pause()); },{signal});
      this.observer = new IntersectionObserver(entries => entries.forEach(e => {if(!e.isIntersecting)e.target.pause();}),{threshold:0});
      this.videos.forEach(v => this.observer.observe(v));
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); this.videos?.forEach(v => v.pause()); }
  }
  const script = url => new Promise((resolve,reject) => { const s=document.createElement('script');s.src=url;s.onload=resolve;s.onerror=reject;document.head.append(s); });
  class Gallery extends HTMLElement {
    async connectedCallback() {
      this.generation=(this.generation||0)+1;const generation=this.generation;
      this.abort = new AbortController(); const signal = this.abort.signal;
      this.dialog = this.querySelector('dialog');
      this.querySelectorAll('.cx-flippable').forEach(shot=>{
        const toggle=shot.querySelector('.cx-flip-toggle');
        const setFlip=flipped=>{
          shot.classList.toggle('cx-flipped',flipped);
          toggle.setAttribute('aria-pressed',String(flipped));
          shot.querySelector('.cx-front').tabIndex=flipped?-1:0;
          shot.querySelector('.cx-back').tabIndex=flipped?0:-1;
          shot.querySelector('.cx-front').setAttribute('aria-hidden',String(flipped));
          shot.querySelector('.cx-back').setAttribute('aria-hidden',String(!flipped));
        };
        toggle.addEventListener('click',()=>setFlip(!shot.classList.contains('cx-flipped')),{signal});
        shot.querySelector('.cx-flip-scene').addEventListener('pointerenter',e=>{if(e.pointerType==='mouse')setFlip(true);},{signal});
        shot.querySelector('.cx-flip-scene').addEventListener('pointerleave',e=>{if(e.pointerType==='mouse'&&!shot.contains(document.activeElement))setFlip(false);},{signal});
      });

      this.querySelectorAll('[data-image]').forEach(button => button.addEventListener('click', () => {
        this.returnFocus=button; const img=this.dialog.querySelector('img');
        img.src=button.dataset.image;img.alt=button.dataset.caption;
        this.dialog.querySelector('p').textContent=button.dataset.caption;
        const link=this.dialog.querySelector('a');link.hidden=!button.dataset.link;
        if(button.dataset.link)link.href=button.dataset.link;
        this.dialog.showModal();
      },{signal}));
      this.dialog.querySelector('.cx-close').addEventListener('click',()=>this.dialog.close(),{signal});
      this.dialog.addEventListener('click',e=>{if(e.target===this.dialog){const b=this.dialog.getBoundingClientRect();if(e.clientX<b.left||e.clientX>b.right||e.clientY<b.top||e.clientY>b.bottom)this.dialog.close();}},{signal});
      this.dialog.addEventListener('close',()=>this.returnFocus?.focus({preventScroll:true}),{signal});
      try {
        if(!window.cahaXmasMotion)window.cahaXmasMotion=(async()=>{
          if(!window.gsap)await (window.cahaFetesGSAP || script(this.dataset.gsap));
          if(!window.ScrollTrigger)await script(this.dataset.scrolltrigger);
          window.gsap.registerPlugin(window.ScrollTrigger);
        })().catch(e=>{window.cahaXmasMotion=null;throw e;});
        await window.cahaXmasMotion;
        if(!this.isConnected||this.generation!==generation)return;
        this.mm=gsap.matchMedia();
        this.mm.add('(min-width:1000px) and (prefers-reduced-motion:no-preference)',()=>{
          const copy=this.querySelector('.cx-gallery-copy');const grid=this.querySelector('.cx-gallery-grid');
          ScrollTrigger.create({trigger:grid,start:'top 110px',end:()=>'+='+Math.max(0,grid.offsetHeight-copy.offsetHeight),pin:copy,pinSpacing:false,invalidateOnRefresh:true});
          this.querySelectorAll('.cx-shot').forEach(shot=>gsap.from(shot,{y:36,scale:.97,scrollTrigger:{trigger:shot,start:'top 95%',end:'top 55%',scrub:1}}));
        },this);
        this.querySelectorAll('img').forEach(img=>img.addEventListener('load',()=>ScrollTrigger.refresh(),{once:true,signal}));
      } catch {}
    }
    disconnectedCallback() { this.generation++;this.abort?.abort();this.mm?.revert();if(this.dialog?.open)this.dialog.close(); }
  }
  customElements.define('caha-xmas-shelf',Shelf);
  customElements.define('caha-xmas-films',Films);
  customElements.define('caha-xmas-gallery',Gallery);
})();

(() => {
  if (customElements.get('caha-christmas-magic')) return;
  class ChristmasMagic extends HTMLElement {
    connectedCallback() {
      this.host=this.parentElement;this.setAttribute('aria-hidden','true');
      this.abort=new AbortController();const signal=this.abort.signal;
      this.reduced=matchMedia('(prefers-reduced-motion: reduce)');
      this.innerHTML='<div class="cm-lights">'+Array.from({length:16},(_,i)=>'<i style="--i:'+i+'"></i>').join('')+'</div><div class="cm-ornaments"><i></i><i></i><i></i></div><div class="cm-stars">'+Array.from({length:8},(_,i)=>'<i style="--i:'+i+'"></i>').join('')+'</div>';
      this.host.classList.add('cm-host');
      this.observer=new IntersectionObserver(entries=>{
        this.classList.toggle('cm-visible',entries[0].isIntersecting);
        if(entries[0].isIntersecting&&!this.entered){this.entered=true;this.enter();}
      },{threshold:.08});this.observer.observe(this.host);
      this.host.addEventListener('caha:gift-open',e=>this.spark(e.detail.index),{signal});
      document.addEventListener('visibilitychange',()=>this.classList.toggle('cm-hidden',document.hidden),{signal});
      document.addEventListener('caha:motion-state',e=>{if(e.detail.paused)this.finish();},{signal});
      this.reduced.addEventListener('change',()=>this.finish(),{signal});
    }
    enter(){
      if(this.reduced.matches||document.documentElement.classList.contains('caha-motion-paused'))return;
      this.animations=[...this.host.querySelectorAll('.cx-heading,.cx-toolbar,.cx-film')].map((node,i)=>node.animate([{transform:'translateY(24px)'},{transform:'translateY(0)'}],{duration:850,delay:i*90,easing:'cubic-bezier(.2,.7,.2,1)'}));
    }
    spark(index){
      if(index<0||this.reduced.matches||document.documentElement.classList.contains('caha-motion-paused'))return;
      this.burst?.remove();clearTimeout(this.burstTimer);
      const card=this.host.querySelectorAll('.fc-card')[index];if(!card)return;
      this.burst=document.createElement('span');this.burst.className='cm-burst';this.burst.setAttribute('aria-hidden','true');
      this.burst.innerHTML=Array.from({length:8},(_,i)=>'<i style="--r:'+i*45+'deg"></i>').join('');
      card.append(this.burst);this.burstTimer=setTimeout(()=>this.burst?.remove(),1100);
    }
    finish(){this.animations?.forEach(a=>a.finish());this.burst?.remove();}
    disconnectedCallback(){this.abort?.abort();this.observer?.disconnect();this.animations?.forEach(a=>a.cancel());clearTimeout(this.burstTimer);this.burst?.remove();this.host.classList.remove('cm-host');}
  }
  customElements.define('caha-christmas-magic',ChristmasMagic);
  const mount=root=>root.querySelectorAll('caha-fetes-hero,.cx-section').forEach(host=>{if(!host.querySelector('caha-christmas-magic'))host.prepend(document.createElement('caha-christmas-magic'));});
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>mount(document),{once:true});else mount(document);
  document.addEventListener('shopify:section:load',e=>mount(e.target));
})();
